import { cloneElement, isValidElement, ReactElement, ReactNode, useMemo } from 'react';

import {
  IRoutingVersionsFilter,
  IRoutingVersionsTableColumns,
  useGetRoutingConfigVersionsQuery,
} from '@entities/routing/config-version';
import { FilterSelect, useFilter } from '@features/table-filter';
import ErrorMessage from '@shared/components/ErrorMessage';
import Loading from '@shared/components/Loading';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { Pagination, PaginationRows } from '@shared/components/Pagination';
import {
  Table,
  TBody,
  THead,
  TableActions,
  TableActionsItem,
  TableContainer,
  TableProvider,
} from '@shared/components/Table';

import { IRoutingVersionRow, useTableSettings } from './settings/useTableSettings';
import './history.css';

const STATUS_FILTER_OPTIONS = [
  { id: 'published', label: 'Active' },
  { id: 'draft', label: 'Draft' },
  { id: 'archived', label: 'Archived' },
];

const RoutingHistoryWidget = () => {
  const filter = useFilter<IRoutingVersionsFilter>();
  const { data, isLoading, isFetching, isError, error } = useGetRoutingConfigVersionsQuery(
    filter.currentFilter.toString(),
  );

  // filter[status] is applied server-side (CSP-646), pagination totals included.
  const rows = useMemo<IRoutingVersionRow[]>(
    () => (data?.data ?? []).map(version => ({ ...version, id: String(version.id) })),
    [data],
  );

  const { columnTitles, renderItem } = useTableSettings();

  const renderRow = (version: IRoutingVersionRow, defaultRow: ReactNode) => {
    if (version.status !== 'published' || !isValidElement(defaultRow)) return defaultRow;
    return cloneElement(defaultRow as ReactElement<{ className?: string }>, {
      className: 'routing-version-row--active',
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorMessage error={error} />;
  if (!data) return null;

  return (
    <>
      {isFetching && <Loading />}

      <TableProvider<IRoutingVersionsTableColumns> columnTitles={columnTitles} {...filter}>
        <TableActions>
          <TableActionsItem>
            <FilterSelect<IRoutingVersionsTableColumns>
              name='status'
              options={STATUS_FILTER_OPTIONS}
            />
          </TableActionsItem>
        </TableActions>

        <TableContainer id='routing-history-table'>
          <Table>
            <THead<IRoutingVersionsTableColumns>
              renderItem={key => (key === 'status' ? <span className='routing-status-col-marker' /> : null)}
            />
            <TBody items={rows} renderItem={renderItem} renderRow={renderRow} />
          </Table>
        </TableContainer>
        <div className='pagination-container'>
          <Pagination data={data.meta} />
          <PaginationRows />
        </div>
      </TableProvider>
    </>
  );
};

export default RoutingHistoryWidget;
